import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { generateBubbledPdf } from '../utils/bubbledPdf';
 
function Exports() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
 
  useEffect(() => {
    const load = async () => {
      try {
        const docRef = doc(db, 'projects', projectId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProject(data);
          setBubbles(data.bubbles || []);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [projectId]);
 
  // ── Bubbled PDF Export ──
  const exportBubbbledPDF = async () => {
    if (!project?.pdfURL) return alert('No PDF found for this project.');
    setExporting('bubbled');
    try {
      const response = await fetch(project.pdfURL);
      if (!response.ok) throw new Error(`Failed to fetch PDF (${response.status})`);
      const pdfBytes = await response.arrayBuffer();
      const modifiedPdfBytes = await generateBubbledPdf(pdfBytes, bubbles);
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MIA_BUBBLED_${project?.partNumber || 'drawing'}_Rev${project?.revision || 'A'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Bubbled PDF error:', err);
      alert('Error generating bubbled PDF. Please try again.');
    }
    setExporting('');
  };
 
  // ── CSV Export ──
  const exportCSV = () => {
    setExporting('csv');
    const headers = ['Bubble #', 'Requirement', 'Nominal', 'Tolerance', 'Feature Type', 'Inspection Method', 'Actual', 'Status'];
    const rows = bubbles.map(b => [
      b.number,
      b.requirement || '',
      b.nominal || '',
      b.tolerance || '',
      b.featureType || '',
      b.inspectionMethod || '',
      (project?.actuals?.[b.id]) || '',
      b.status || 'Pending'
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MIA_${project?.partNumber || 'inspection'}_Rev${project?.revision || 'A'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setExporting(''), 1000);
  };
 
  // ── JSON Export ──
  const exportJSON = () => {
    setExporting('json');
    const data = {
      exportedAt: new Date().toISOString(),
      project: {
        partNumber: project?.partNumber,
        revision: project?.revision,
        drawingNumber: project?.drawingNumber,
        material: project?.material,
        finish: project?.finish,
        units: project?.units,
        companyName: project?.companyName,
        inspectorName: project?.inspectorName,
        inspectionDate: project?.inspectionDate,
        jobNumber: project?.jobNumber,
        fileName: project?.fileName,
      },
      bubbles: bubbles.map(b => ({
        number: b.number,
        requirement: b.requirement,
        nominal: b.nominal,
        tolerance: b.tolerance,
        featureType: b.featureType,
        inspectionMethod: b.inspectionMethod,
        actual: project?.actuals?.[b.id] || '',
        status: b.status,
      })),
      summary: {
        total: bubbles.length,
        pass: bubbles.filter(b => b.status === 'Pass').length,
        fail: bubbles.filter(b => b.status === 'Fail').length,
        pending: bubbles.filter(b => b.status === 'Pending').length,
        na: bubbles.filter(b => b.status === 'N/A').length,
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MIA_${project?.partNumber || 'inspection'}_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setExporting(''), 1000);
  };
 
  const passCount = bubbles.filter(b => b.status === 'Pass').length;
  const failCount = bubbles.filter(b => b.status === 'Fail').length;
  const pendingCount = bubbles.filter(b => b.status === 'Pending').length;
  const overallResult = failCount > 0 ? 'FAIL' : pendingCount > 0 ? 'PENDING' : bubbles.length > 0 ? 'PASS' : 'PENDING';
 
  return (
    <div style={styles.page}>
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br />Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.navItem} onClick={() => navigate('/')}><span>⬆</span> Upload Print</div>
          <div style={styles.navItem} onClick={() => navigate(`/review/${projectId}`)}><span>◎</span> Review Bubbles</div>
          <div style={styles.navItem} onClick={() => navigate(`/fai/${projectId}`)}><span>☰</span> FAI Document</div>
          <div style={styles.navItem} onClick={() => navigate(`/final/${projectId}`)}><span>✓</span> Final Inspection</div>
          <div style={{ ...styles.navItem, ...styles.navActive }}><span>↓</span> Exports</div>
        </nav>
      </div>
 
      <div style={styles.main}>
        <div style={styles.topbar}>
          <div style={styles.stepperRow}>
            <div style={styles.stepDone}>✓ Upload PDF</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Review Bubbles</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ FAI Document</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Final Inspection</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepActive}>Export Package</div>
          </div>
          <div style={styles.topbarRight}>
            <button style={styles.btnOutline} onClick={() => navigate(`/final/${projectId}`)}>← Back</button>
          </div>
        </div>
 
        <div style={styles.content}>
          {loading && <div style={styles.loadingMsg}>Loading project data...</div>}
          {!loading && project && (
            <div style={styles.summaryCard}>
              <div style={styles.summaryLeft}>
                <div style={styles.summaryTitle}>{project.partNumber} — Rev {project.revision || '—'}</div>
                <div style={styles.summaryMeta}>{project.fileName} · {project.material || '—'} · {project.units}</div>
                <div style={styles.summaryStats}>
                  <span style={styles.statPass}>✓ {passCount} Pass</span>
                  <span style={styles.statFail}>✕ {failCount} Fail</span>
                  <span style={styles.statPending}>⏳ {pendingCount} Pending</span>
                  <span style={styles.statTotal}>Total: {bubbles.length}</span>
                </div>
              </div>
              <div style={{
                ...styles.resultBadge,
                background: overallResult === 'PASS' ? '#16a34a' : overallResult === 'FAIL' ? '#dc2626' : '#f59e0b'
              }}>
                {overallResult}
              </div>
            </div>
          )}
 
          {!loading && (
            <>
              <div style={styles.sectionTitle}>Export Options</div>
              <div style={styles.exportGrid}>
 
                <div style={{ ...styles.exportCard, borderLeft: '3px solid #2563EB' }}>
                  <div style={{ ...styles.exportIconWrap, background: '#eff6ff' }}>
                    <span style={styles.exportIcon}>📄</span>
                  </div>
                  <div style={styles.exportInfo}>
                    <div style={styles.exportTitle}>Bubbled Drawing PDF</div>
                    <div style={styles.exportDesc}>Your original drawing with all numbered inspection bubbles and leader lines overlaid directly on the PDF.</div>
                    <div style={styles.exportMeta}>{bubbles.length} bubbles · PDF</div>
                  </div>
                  <button
                    style={{ ...styles.exportBtn, background: exporting === 'bubbled' ? '#93c5fd' : '#2563EB' }}
                    onClick={exportBubbbledPDF}
                    disabled={exporting === 'bubbled'}
                  >
                    {exporting === 'bubbled' ? '⏳ Generating...' : '⬇ Download Bubbled PDF'}
                  </button>
                </div>
 
                <div style={styles.exportCard}>
                  <div style={{ ...styles.exportIconWrap, background: '#eff6ff' }}>
                    <span style={styles.exportIcon}>📋</span>
                  </div>
                  <div style={styles.exportInfo}>
                    <div style={styles.exportTitle}>FAI Report</div>
                    <div style={styles.exportDesc}>First Article Inspection document with all characteristics, nominals, tolerances, and signature blocks.</div>
                    <div style={styles.exportMeta}>{bubbles.length} characteristics · PDF</div>
                  </div>
                  <button style={styles.exportBtn} onClick={() => navigate(`/fai/${projectId}`)}>View &amp; Print →</button>
                </div>
 
                <div style={styles.exportCard}>
                  <div style={{ ...styles.exportIconWrap, background: '#f0fdf4' }}>
                    <span style={styles.exportIcon}>✅</span>
                  </div>
                  <div style={styles.exportInfo}>
                    <div style={styles.exportTitle}>Final Inspection Report</div>
                    <div style={styles.exportDesc}>Complete inspection results with actual measurements, Pass/Fail status, NCR details, and overall conformance result.</div>
                    <div style={styles.exportMeta}>{bubbles.length} characteristics · PDF</div>
                  </div>
                  <button style={styles.exportBtn} onClick={() => navigate(`/final/${projectId}`)}>View &amp; Print →</button>
                </div>
 
                <div style={styles.exportCard}>
                  <div style={{ ...styles.exportIconWrap, background: '#f0fdf4' }}>
                    <span style={styles.exportIcon}>📊</span>
                  </div>
                  <div style={styles.exportInfo}>
                    <div style={styles.exportTitle}>Spreadsheet Export</div>
                    <div style={styles.exportDesc}>All inspection data in CSV format. Opens in Excel, Google Sheets, or any spreadsheet app.</div>
                    <div style={styles.exportMeta}>{bubbles.length} rows · CSV</div>
                  </div>
                  <button style={{ ...styles.exportBtn, background: '#16a34a' }} onClick={exportCSV} disabled={exporting === 'csv'}>
                    {exporting === 'csv' ? '⬇ Downloading...' : '⬇ Download CSV'}
                  </button>
                </div>
 
                <div style={styles.exportCard}>
                  <div style={{ ...styles.exportIconWrap, background: '#fdf4ff' }}>
                    <span style={styles.exportIcon}>💾</span>
                  </div>
                  <div style={styles.exportInfo}>
                    <div style={styles.exportTitle}>Full Data Backup</div>
                    <div style={styles.exportDesc}>Complete project data exported as JSON. Includes all bubble positions, inspection data, and metadata.</div>
                    <div style={styles.exportMeta}>Full project · JSON</div>
                  </div>
                  <button style={{ ...styles.exportBtn, background: '#7c3aed' }} onClick={exportJSON} disabled={exporting === 'json'}>
                    {exporting === 'json' ? '⬇ Downloading...' : '⬇ Download JSON'}
                  </button>
                </div>
 
              </div>
 
              <div style={styles.comingSoon}>
                <div style={styles.comingSoonTitle}>Coming Soon</div>
                <div style={styles.comingSoonGrid}>
                  <div style={styles.comingSoonItem}>
                    <span>📦</span>
                    <div>
                      <div style={styles.csItemTitle}>Full Package ZIP</div>
                      <div style={styles.csItemDesc}>All documents bundled in one download</div>
                    </div>
                  </div>
                  <div style={styles.comingSoonItem}>
                    <span>📧</span>
                    <div>
                      <div style={styles.csItemTitle}>Email Package</div>
                      <div style={styles.csItemDesc}>Send inspection package directly to customer</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { height: '52px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 },
  stepperRow: { display: 'flex', alignItems: 'center', gap: '0', flex: 1 },
  stepDone: { fontSize: '11px', fontWeight: '500', color: '#2563EB', whiteSpace: 'nowrap' },
  stepActive: { fontSize: '11px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' },
  stepLine: { width: '20px', height: '1.5px', background: '#2563EB', margin: '0 4px' },
  topbarRight: { display: 'flex', gap: '8px' },
  btnOutline: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  content: { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  loadingMsg: { fontSize: '14px', color: '#6b7280', padding: '20px 0' },
  summaryCard: { background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '18px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  summaryLeft: { display: 'flex', flexDirection: 'column', gap: '5px' },
  summaryTitle: { fontSize: '16px', fontWeight: '700', color: '#111827' },
  summaryMeta: { fontSize: '12px', color: '#9ca3af' },
  summaryStats: { display: 'flex', gap: '16px', marginTop: '4px' },
  statPass: { fontSize: '12.5px', fontWeight: '600', color: '#16a34a' },
  statFail: { fontSize: '12.5px', fontWeight: '600', color: '#dc2626' },
  statPending: { fontSize: '12.5px', fontWeight: '600', color: '#f59e0b' },
  statTotal: { fontSize: '12.5px', color: '#9ca3af' },
  resultBadge: { color: '#fff', fontWeight: '800', fontSize: '16px', padding: '8px 20px', borderRadius: '8px', letterSpacing: '0.05em' },
  sectionTitle: { fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' },
  exportGrid: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' },
  exportCard: { background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px' },
  exportIconWrap: { width: '48px', height: '48px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  exportIcon: { fontSize: '22px' },
  exportInfo: { flex: 1 },
  exportTitle: { fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '3px' },
  exportDesc: { fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginBottom: '4px' },
  exportMeta: { fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' },
  exportBtn: { background: '#2563EB', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 },
  comingSoon: { background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px' },
  comingSoonTitle: { fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' },
  comingSoonGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  comingSoonItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '20px' },
  csItemTitle: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  csItemDesc: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
};

export default Exports;